import React, { useState } from 'react';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { stamp } from '../lib/format.js';
import { Alert, Card, CardSkeleton, Field, Pill, useAsync } from './ui.jsx';

const ROLE_OPTIONS = [
  ['tenant_admin', 'Admin'],
  ['accounts', 'Accounts'],
  ['sales_counter', 'Sales counter'],
  ['godown_operator', 'Godown'],
];
const ROLE_NOTE = {
  tenant_admin: 'everything, including users and settings',
  accounts: 'invoices, credit notes, approvals, Tally sync and reports',
  sales_counter: 'delivery orders, invoices, customers and price checks',
  godown_operator: 'despatches and stock at the godowns assigned to them',
};
const BLANK = { full_name: '', email: '', role: 'sales_counter', password: '', godown_ids: [] };
const toggle = (ids, id) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

/**
 * Who can sign in to this workspace, and as what. The administrator adds a
 * colleague with a temporary password, which the colleague replaces at first
 * sign-in; a forgotten one is replaced through a one-time link, not by phone.
 * The server keeps the last active administrator from being demoted or
 * switched off (routes/masters.js).
 */
export default function UserSettings({ setError, onSaved }) {
  const session = useSession();
  const users = useAsync(() => api.get('/api/users'), []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [resetLink, setResetLink] = useState(null);
  const godowns = session.godowns || [];

  const create = async () => {
    setError(null);
    const email = form.email.trim().toLowerCase();
    if (form.full_name.trim().length < 2) { setError("Enter the person's name."); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setError('Enter a valid email address: it is their sign-in id.'); return; }
    if (form.password.length < 10) { setError('The temporary password must be at least 10 characters.'); return; }
    if (form.role === 'godown_operator' && !form.godown_ids.length) { setError('Choose at least one godown for a godown user.'); return; }
    setBusy(true);
    try {
      await api.post('/api/users', {
        full_name: form.full_name.trim(), email, password: form.password, role: form.role,
        godown_ids: form.role === 'godown_operator' ? form.godown_ids : [],
      });
      onSaved(`User ${email}`);
      setForm(BLANK); setAdding(false); users.reload();
    } catch (e) {
      setError(e.status === 409 ? `${email} already has an InvIT sign-in.` : e.message);
    } finally { setBusy(false); }
  };

  const update = async (u, body, label) => {
    setError(null);
    try { await api.patch(`/api/users/${u.id}`, body); onSaved(label); }
    catch (e) { setError(e.message); }
    users.reload();
  };

  const reset = async (u) => {
    setError(null); setResetLink(null);
    try { setResetLink(await api.post(`/api/users/${u.id}/reset-password`)); }
    catch (e) { setError(e.message); }
  };

  const godownChecks = (ids, onToggle) => (
    <div className="inline" style={{ gap: 12, flexWrap: 'wrap' }}>
      {godowns.map((g) => (
        <label key={g.id} style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0, fontWeight: 400, color: 'var(--ink)' }}>
          <input type="checkbox" style={{ width: 16 }} checked={ids.includes(g.id)} onChange={() => onToggle(g.id)} /> {g.name}
        </label>
      ))}
    </div>
  );

  return (
    <Card title="Users" note="Who can sign in to this workspace, and what each role can do"
      actions={!adding && (
        <button className="btn small" onClick={() => { setAdding(true); setResetLink(null); }}>＋ Add user</button>
      )}>
      {adding && (
        <div style={{ borderBottom: '1px solid var(--line)', paddingBottom: 14, marginBottom: 14 }}>
          <div className="row">
            <Field label="Name">
              <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full name" />
            </Field>
            <Field label="Email" hint="their sign-in id">
              <input type="email" value={form.email} autoComplete="off"
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.in" />
            </Field>
            <Field label="Role">
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Temporary password" hint="10+ characters, changed at first sign-in">
              <input type="text" value={form.password} autoComplete="new-password" spellCheck={false}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </Field>
          </div>
          <div className="card-note">{ROLE_OPTIONS.find(([v]) => v === form.role)[1]}: {ROLE_NOTE[form.role]}.</div>
          {form.role === 'godown_operator' && (
            <div style={{ marginTop: 10 }}>
              <div className="card-note" style={{ marginBottom: 6 }}>Godowns this user works at</div>
              {godownChecks(form.godown_ids, (id) => setForm({ ...form, godown_ids: toggle(form.godown_ids, id) }))}
            </div>
          )}
          <div className="inline" style={{ marginTop: 12 }}>
            <button className="btn" disabled={busy} onClick={create}>{busy ? 'Adding…' : 'Add user'}</button>
            <button className="btn ghost" onClick={() => { setAdding(false); setForm(BLANK); }}>Cancel</button>
          </div>
        </div>
      )}

      {resetLink && (
        <Alert kind="info">
          Password reset link for {resetLink.user.email}. Hand it over directly; it works once and expires
          in {resetLink.expires_in_minutes} minutes.
          <div className="mono" style={{ marginTop: 6, userSelect: 'all', wordBreak: 'break-all' }}>{resetLink.reset_link}</div>
        </Alert>
      )}

      <Alert onRetry={users.reload}>{users.error}</Alert>
      {users.loading && !users.data && <CardSkeleton lines={3} />}
      {users.data && (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th style={{ width: 160 }}>Role</th><th>Godowns</th><th>Last sign-in</th><th>Status</th><th /></tr></thead>
            <tbody>
              {users.data.users.map((u) => {
                const self = u.id === session.user.id;
                const ids = u.godowns.map((g) => g.id);
                return (
                  <tr key={u.id} style={u.is_active ? undefined : { opacity: 0.6 }}>
                    <td>{u.full_name}{self && <span className="muted"> (you)</span>}<div className="sub">{u.email}</div></td>
                    <td>
                      <select value={u.role} aria-label={`Role of ${u.full_name}`}
                        onChange={(e) => update(u, { role: e.target.value }, `${u.full_name}'s role`)}>
                        {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td>
                      {u.role === 'godown_operator'
                        ? godownChecks(ids, (id) => update(u, { godown_ids: toggle(ids, id) }, `${u.full_name}'s godowns`))
                        : <span className="muted">all</span>}
                    </td>
                    <td>{u.last_login_at ? stamp(u.last_login_at) : <span className="muted">never</span>}</td>
                    <td><Pill value={u.is_active ? 'active' : 'cancelled'} label={u.is_active ? 'active' : 'switched off'} /></td>
                    <td className="r" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn small ghost" onClick={() => reset(u)}>Reset password</button>
                      {!self && (
                        <button className="btn small ghost"
                          onClick={() => update(u, { is_active: !u.is_active },
                            u.is_active ? `${u.full_name} switched off` : `${u.full_name} switched on`)}>
                          {u.is_active ? 'Switch off' : 'Switch on'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
